/**
 * IntakyActivityDrawer — slide-in right panel showing the full activity
 * timeline for a single intake. Opens when a table row is clicked.
 */
import {
  ArrowPathIcon,
  BellAlertIcon,
  CalendarIcon,
  ChatBubbleLeftIcon,
  ClockIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import React, { useEffect, useRef, useState } from 'react';

import { INTAKY_COLORS, INTAKY_FONT, INTAKY_STATUS_MAP, statusToTierConfig } from './intaky-tokens';
import type { IntakyMapIntake } from './IntakyMap';

const API_BASE = import.meta.env.VITE_APP_DJANGO_BACKEND_HOST as string;

interface Activity {
  id: string;
  intake_id: string;
  activity_type: string;
  headline: string;
  detail: string;
  occurred_at: string;
  metadata: Record<string, string>;
}

interface IntakyActivityDrawerProps {
  intake: IntakyMapIntake;
  firmId: string | undefined;
  /** Bumped by parent on every SSE event — triggers refetch */
  version?: number;
  onClose: () => void;
}

const ICON_SIZE = 14;

function activityIcon(type: string): React.ReactNode {
  const s = { width: ICON_SIZE, height: ICON_SIZE };
  switch (type) {
    case 'status_changed': return <ArrowPathIcon style={s} />;
    case 'chase_note_added': return <PencilSquareIcon style={s} />;
    case 'loe_sent': return <DocumentArrowUpIcon style={s} />;
    case 'loe_signed': return <SparklesIcon style={s} />;
    case 'callback_scheduled': return <CalendarIcon style={s} />;
    case 'callback_cancelled': return <XMarkIcon style={s} />;
    case 'chase_sms_sent': return <ChatBubbleLeftIcon style={s} />;
    case 'intake_created': return <PlusCircleIcon style={s} />;
    case 'screening_updated': return <ClockIcon style={s} />;
    case 'document_uploaded': return <DocumentArrowUpIcon style={s} />;
    case 'escalated': return <ExclamationTriangleIcon style={s} />;
    default: return <BellAlertIcon style={s} />;
  }
}

const ACTIVITY_LABELS: Record<string, string> = {
  status_changed: 'Status Change',
  chase_note_added: 'Note Added',
  loe_sent: 'Letter of Engagement Sent',
  loe_signed: 'Retainer Signed',
  callback_scheduled: 'Callback Scheduled',
  callback_cancelled: 'Callback Cancelled',
  chase_sms_sent: 'SMS Sent',
  intake_created: 'Intake Created',
  screening_updated: 'Screening Updated',
  document_uploaded: 'Document Uploaded',
  escalated: 'Escalated',
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const IntakyActivityDrawer: React.FC<IntakyActivityDrawerProps> = ({
  intake,
  firmId,
  version,
  onClose,
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const tierConfig = statusToTierConfig(intake.status);
  const statusInfo = INTAKY_STATUS_MAP[intake.status];

  const resolvedFirmId = intake.firm_id ?? firmId;

  useEffect(() => {
    if (!resolvedFirmId) {
      setError('No firm context available');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/intaky/${resolvedFirmId}/intakes/${intake.id}/timeline/?limit=100`)
      .then((r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then((data) => {
        setActivities(data.activities ?? []);
        setLoading(false);
      })
      .catch((e) => {
        setError(`Failed to load: ${e.message}`);
        setLoading(false);
      });
  }, [resolvedFirmId, intake.id, version]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(3px)',
        animation: 'intaky-tooltip-in 0.2s ease-out',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 520,
          maxWidth: '90vw',
          background: '#FAFBFC',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: INTAKY_FONT.family,
          animation: 'intaky-drawer-slide 0.25s ease-out',
        }}
      >
        {/* ── Header ── */}
        <div style={{ background: 'white', borderBottom: `1px solid ${INTAKY_COLORS.border}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px 0' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: tierConfig.bg,
                color: tierConfig.color,
                fontSize: 11,
                fontWeight: 700,
                padding: '4px 12px',
                borderRadius: 9999,
                letterSpacing: '0.02em',
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: tierConfig.dot }} />
              {statusInfo?.label ?? intake.status}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: '#F1F5F9',
                border: 'none',
                cursor: 'pointer',
                width: 28,
                height: 28,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: INTAKY_COLORS.textMuted,
              }}
            >
              <XMarkIcon style={{ width: 16, height: 16 }} />
            </button>
          </div>

          <div style={{ padding: '12px 24px 0' }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: INTAKY_COLORS.textPrimary, letterSpacing: '-0.03em', lineHeight: 1.2 }}>
              {intake.client_display}
            </h2>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 24px 16px' }}>
            {intake.case_type && (
              <span style={{ fontSize: 11, fontWeight: 600, color: INTAKY_COLORS.textSecondary, background: '#F1F5F9', padding: '3px 10px', borderRadius: 6 }}>
                {intake.case_type}
              </span>
            )}
            {intake.state && (
              <span style={{ fontSize: 11, fontWeight: 600, color: INTAKY_COLORS.textSecondary, background: '#F1F5F9', padding: '3px 10px', borderRadius: 6 }}>
                {intake.city ? `${intake.city}, ${intake.state}` : intake.state}
              </span>
            )}
            {intake.referral_source && (
              <span style={{ fontSize: 11, fontWeight: 500, color: INTAKY_COLORS.textMuted, background: '#F1F5F9', padding: '3px 10px', borderRadius: 6 }}>
                Referred by {intake.referral_source}
              </span>
            )}
          </div>
        </div>

        {/* ── Activity Timeline ── */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px 32px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: INTAKY_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
            Activity Timeline
          </div>

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 10 }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${INTAKY_COLORS.border}`, borderTopColor: tierConfig.dot, animation: 'intaky-spin 0.75s linear infinite' }} />
              <span style={{ fontSize: 13, color: INTAKY_COLORS.textMuted }}>Loading activity...</span>
            </div>
          )}

          {error && (
            <div style={{ padding: '32px 0', textAlign: 'center', color: '#DC2626', fontSize: 13 }}>
              {error}
            </div>
          )}

          {!loading && !error && activities.length === 0 && (
            <div style={{ padding: '48px 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: INTAKY_COLORS.textPrimary, marginBottom: 4 }}>
                No activity yet
              </div>
              <div style={{ fontSize: 12, color: INTAKY_COLORS.textMuted }}>
                Updates will appear here as your Finch team works this lead.
              </div>
            </div>
          )}

          {!loading && !error && activities.length > 0 && (
            <div style={{ position: 'relative' }}>
              {/* Timeline line */}
              <div style={{ position: 'absolute', left: 15, top: 8, bottom: 8, width: 1, background: INTAKY_COLORS.border }} />

              {activities.map((act, idx) => {
                const label = ACTIVITY_LABELS[act.activity_type] ?? act.activity_type;
                const headline = act.headline.replace(/\bAuto\s+/gi, '');
                const detail = (act.detail || '').replace(/\bAuto\s+/gi, '');
                const isFirst = idx === 0;

                return (
                  <div
                    key={act.id}
                    style={{
                      position: 'relative',
                      paddingLeft: 42,
                      paddingBottom: 20,
                    }}
                  >
                    {/* Timeline dot with icon */}
                    <div
                      style={{
                        position: 'absolute',
                        left: 6,
                        top: 4,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        background: isFirst ? tierConfig.bg : '#F1F5F9',
                        border: `2px solid ${isFirst ? tierConfig.dot : INTAKY_COLORS.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isFirst ? tierConfig.color : INTAKY_COLORS.textMuted,
                        zIndex: 1,
                      }}
                    >
                      {activityIcon(act.activity_type)}
                    </div>

                    {/* Card */}
                    <div
                      style={{
                        background: 'white',
                        borderRadius: 10,
                        padding: '12px 16px',
                        border: `1px solid ${isFirst ? tierConfig.bg : INTAKY_COLORS.border}`,
                        boxShadow: isFirst ? `0 1px 4px ${tierConfig.ring}` : 'none',
                      }}
                    >
                      {/* Type + time */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: INTAKY_COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {label}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 500, color: INTAKY_COLORS.textMuted, whiteSpace: 'nowrap' }}>
                          {formatTime(act.occurred_at)}
                        </span>
                      </div>

                      {/* Headline */}
                      <div style={{ fontSize: 14, fontWeight: 700, color: INTAKY_COLORS.textPrimary, lineHeight: 1.4 }}>
                        {headline}
                      </div>

                      {/* Detail text */}
                      {detail && (
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 13,
                            color: '#374151',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            background: '#F8FAFC',
                            padding: '10px 14px',
                            borderRadius: 8,
                          }}
                        >
                          {detail}
                        </div>
                      )}

                      {/* Metadata */}
                      {Object.keys(act.metadata).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                          {Object.entries(act.metadata).map(([k, v]) => (
                            <span
                              key={k}
                              style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: INTAKY_COLORS.textMuted,
                                background: '#F1F5F9',
                                padding: '2px 8px',
                                borderRadius: 4,
                              }}
                            >
                              {k.replace(/_/g, ' ')}: {v}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
